import { useEffect, useMemo, useState } from "react";
import type { SolutionBlueprintV1 } from "@clickdialog/solution-contract";
import {
  PlatformApiError,
  type PlatformApiClient,
  type SolutionFactoryActivationState,
  type SolutionFactoryCertification,
  type SolutionFactoryDraft,
  type SolutionFactoryInstance,
  type SolutionFactoryProvisioningApplyDescriptor,
  type SolutionFactoryProvisioningApplyState,
  type SolutionFactoryProvisioningPlan,
  type SolutionFactoryProvisioningPlanRevision,
} from "../api";
import "./solution-studio.css";
import { FreeformSolutionCanvas } from "./FreeformSolutionCanvas";
import {
  BadgeCheck,
  Bot,
  BookOpen,
  CalendarDays,
  Headphones,
  MessageCircle,
  Layers3,
  Phone,
  Play,
  Radio,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Settings2,
  X,
  Send,
  Sparkles,
  UserRound,
  Workflow,
  Wrench,
} from "lucide-react";
import {
  activeEdgeKeys,
  activeNodeKeys,
  buildSolutionDemoScenario,
  eventsForCorrelation,
  runtimeEventsOnly,
  projectSolutionBlueprint,
  runtimeTraceSummaries,
  SOLUTION_BLUEPRINT,
  SOLUTION_EDGES,
  SOLUTION_NODES,
  summarizeExecution,
  updateSolutionLifecycle,
  type PulseExecutionEvent,
  type SolutionEdgeDefinition,
  type SolutionNodeDefinition,
  type SolutionNodeKey,
} from "./solution-model";

type StudioMode = "build" | "simulate" | "live";
type LiveConnectionState = "disconnected" | "connecting" | "connected";

const legacyNodeIcon: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  phone: Phone,
  conversation: MessageCircle,
  agent: Bot,
  customer: UserRound,
  knowledge: BookOpen,
  scheduling: CalendarDays,
  automation: Workflow,
  whatsapp: Send,
  human: Headphones,
};

function iconForNode(
  node: Pick<SolutionNodeDefinition, "key" | "kind" | "channelKey" | "capabilityKey">,
): React.ComponentType<{ size?: number; strokeWidth?: number }> {
  if (node.channelKey === "voice") return Phone;
  if (node.channelKey === "meta-whatsapp") return Send;
  if (node.capabilityKey === "customer.identity.read") return UserRound;
  if (node.capabilityKey === "scheduling.appointment") return CalendarDays;
  return legacyNodeIcon[node.key] ??
    (node.kind === "conversation"
      ? MessageCircle
      : node.kind === "agent"
        ? Bot
        : node.kind === "knowledge"
          ? BookOpen
          : node.kind === "automation"
            ? Workflow
            : node.kind === "human"
              ? Headphones
              : node.kind === "business"
                ? UserRound
                : Send);
}

function NodeCard({
  node,
  active,
  selected,
  editable,
  onSelect,
  onPointerDown,
}: {
  node: SolutionNodeDefinition;
  active: boolean;
  selected: boolean;
  editable: boolean;
  onSelect: () => void;
  onPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
}) {
  const Icon = iconForNode(node);
  return <button
    type="button"
    className="solution-node"
    data-kind={node.kind}
    data-active={active ? "true" : "false"}
    data-selected={selected ? "true" : "false"}
    data-editable={editable ? "true" : "false"}
    style={{ left: `${node.x}%`, top: `${node.y}%` }}
    onPointerDown={onPointerDown}
    onClick={onSelect}
  >
    <span className="solution-node-aura" aria-hidden="true" />
    <span className="solution-node-icon" aria-hidden="true"><Icon size={18} strokeWidth={1.8} /></span>
    <span className="solution-node-copy">
      <small>{node.eyebrow}</small>
      <strong>{node.label}</strong>
      <span>{node.description}</span>
    </span>
  </button>;
}

function PulseGraph({
  events,
  nodes,
  edges,
  selectedNode,
  editable,
  zoom,
  onSelectNode,
  onMoveNode,
}: {
  events: readonly PulseExecutionEvent[];
  nodes: readonly SolutionNodeDefinition[];
  edges: readonly SolutionEdgeDefinition[];
  selectedNode: SolutionNodeKey;
  editable: boolean;
  zoom: number;
  onSelectNode: (node: SolutionNodeKey) => void;
  onMoveNode?: (node: SolutionNodeKey, x: number, y: number) => void;
}) {
  const [dragging, setDragging] = useState<SolutionNodeKey | null>(null);
  const activeNodes = useMemo(() => activeNodeKeys(events), [events]);
  const activeEdges = useMemo(() => activeEdgeKeys(events), [events]);
  const nodesByKey = useMemo(() => new Map(nodes.map((node) => [node.key, node])), [nodes]);

  function movePointer(event: React.PointerEvent<HTMLDivElement>) {
    if (!editable || !dragging || !onMoveNode) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const scaledWidth = rect.width * zoom;
    const scaledHeight = rect.height * zoom;
    const leftInset = (rect.width - scaledWidth) / 2;
    const topInset = (rect.height - scaledHeight) / 2;
    const x = Math.min(
      93,
      Math.max(7, ((event.clientX - rect.left - leftInset) / scaledWidth) * 100),
    );
    const y = Math.min(
      92,
      Math.max(8, ((event.clientY - rect.top - topInset) / scaledHeight) * 100),
    );
    onMoveNode(dragging, x, y);
  }

  return <div
    className="pulse-graph"
    aria-label="Graphe de solution ClickDialog"
    data-editable={editable ? "true" : "false"}
    onPointerMove={movePointer}
    onPointerUp={() => setDragging(null)}
    onPointerCancel={() => setDragging(null)}
  >
    <div
      className="pulse-graph-stage"
      style={{ transform: `scale(${zoom})` }}
    >
      <div className="pulse-grid" aria-hidden="true" />
      <svg className="pulse-edges" viewBox="0 0 1000 560" preserveAspectRatio="none" aria-hidden="true">
        {edges.map((edge) => {
          const from = nodesByKey.get(edge.from)!;
          const to = nodesByKey.get(edge.to)!;
          const x1 = from.x * 10 + 58;
          const y1 = from.y * 5.6;
          const x2 = to.x * 10 - 58;
          const y2 = to.y * 5.6;
          const active = activeEdges.has(edge.key);
          return <g key={edge.key} data-active={active ? "true" : "false"}>
            <line className="pulse-edge-base" x1={x1} y1={y1} x2={x2} y2={y2} />
            <line className="pulse-edge-energy" x1={x1} y1={y1} x2={x2} y2={y2} />
          </g>;
        })}
      </svg>
      {nodes.map((node) => <NodeCard
        key={node.key}
        node={node}
        active={activeNodes.has(node.key)}
        selected={selectedNode === node.key}
        editable={editable}
        onPointerDown={editable ? (event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(node.key);
          onSelectNode(node.key);
        } : undefined}
        onSelect={() => onSelectNode(node.key)}
      />)}
    </div>
  </div>;
}

function compactCorrelationId(value: string): string {
  if (value.length <= 34) return value;
  return `${value.slice(0, 18)}…${value.slice(-12)}`;
}

function EventTimeline({ events, emptyMessage }: { events: readonly PulseExecutionEvent[]; emptyMessage: string }) {
  if (events.length === 0) return <div className="pulse-empty">
    <Radio size={18} aria-hidden="true" />
    <p>{emptyMessage}</p>
  </div>;

  return <ol className="pulse-timeline">
    {[...events].reverse().slice(0, 7).map((event) => <li key={event.eventId} data-phase={event.phase}>
      <span className="pulse-event-dot" aria-hidden="true" />
      <div>
        <div className="pulse-event-heading">
          <strong>{event.label}</strong>
          <span>{event.origin === "runtime" ? "LIVE" : "SIM"}</span>
        </div>
        <p>{event.detail}</p>
        {event.origin === "runtime" && <div className="pulse-event-meta">
          {event.source && <span>{event.source}</span>}
          {event.eventType && <span>{event.eventType}</span>}
          {typeof event.durationMs === "number" && <span>{event.durationMs} ms</span>}
        </div>}
      </div>
    </li>)}
  </ol>;
}

export interface SolutionStudioTargetOrganization {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
}

function SolutionOperationsPanel({
  api,
  certification,
  targetOrganizations,
}: {
  api?: PlatformApiClient;
  certification: SolutionFactoryCertification | null;
  targetOrganizations: readonly SolutionStudioTargetOrganization[];
}) {
  const [organizationId, setOrganizationId] = useState(
    targetOrganizations[0]?.id ?? "",
  );
  const [plan, setPlan] = useState<SolutionFactoryProvisioningPlan | null>(null);
  const [planRevisions, setPlanRevisions] = useState<
    readonly SolutionFactoryProvisioningPlanRevision[]
  >([]);
  const [instances, setInstances] = useState<readonly SolutionFactoryInstance[]>([]);
  const [instance, setInstance] = useState<SolutionFactoryInstance | null>(null);
  const [activation, setActivation] =
    useState<SolutionFactoryActivationState | null>(null);
  const [applyState, setApplyState] =
    useState<SolutionFactoryProvisioningApplyState | null>(null);
  const [agentModel, setAgentModel] = useState("");
  const [agentCredential, setAgentCredential] = useState("");
  const [knowledgeTitle, setKnowledgeTitle] = useState("");
  const [knowledgeText, setKnowledgeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (
      organizationId &&
      targetOrganizations.some((organization) => organization.id === organizationId)
    ) return;
    setOrganizationId(targetOrganizations[0]?.id ?? "");
  }, [organizationId, targetOrganizations]);

  useEffect(() => {
    setPlan(null);
    setPlanRevisions([]);
    setInstances([]);
    setInstance(null);
    setActivation(null);
    setApplyState(null);
    setNotice(null);
    setError(null);
    if (!api || !certification || !organizationId) return;

    let active = true;
    setLoading(true);
    api.solutionFactoryInstances(organizationId)
      .then(async ({ instances: loaded }) => {
        if (!active) return;
        const compatible = loaded.filter(
          (candidate) => candidate.templateKey === certification.templateKey,
        );
        setInstances(compatible);
        const matching = compatible.find(
          (candidate) => candidate.certificationId === certification.id,
        );
        if (!matching) return;
        const [planResult, activationResult, applyResult] = await Promise.all([
          api.solutionFactoryProvisioningPlan(matching.provisioningPlanId),
          api.solutionFactoryActivationState(matching.id),
          api.solutionFactoryProvisioningApply(matching.provisioningPlanId),
        ]);
        if (!active) return;
        setInstance(matching);
        setPlan(planResult.plan);
        setPlanRevisions(planResult.revisions);
        setActivation(activationResult);
        setApplyState(applyResult);
      })
      .catch((cause) => {
        if (!active) return;
        setError(
          cause instanceof PlatformApiError
            ? cause.message
            : "Impossible de charger l'état opérationnel de cette solution.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, certification, organizationId]);

  async function selectInstance(instanceId: string) {
    if (!api) return;
    const selected = instances.find((candidate) => candidate.id === instanceId);
    if (!selected) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const [planResult, activationResult, applyResult] = await Promise.all([
        api.solutionFactoryProvisioningPlan(selected.provisioningPlanId),
        api.solutionFactoryActivationState(selected.id),
        api.solutionFactoryProvisioningApply(selected.provisioningPlanId),
      ]);
      setInstance(selected);
      setPlan(planResult.plan);
      setPlanRevisions(planResult.revisions);
      setActivation(activationResult);
      setApplyState(applyResult);
      setNotice("Instance préparée chargée sans modifier le runtime.");
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de charger cette instance.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function createOrOpenPlan() {
    if (!api || !certification || !organizationId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createSolutionFactoryProvisioningPlan(
        certification.id,
        organizationId,
      );
      const [detail, applyResult] = await Promise.all([
        api.solutionFactoryProvisioningPlan(result.plan.id),
        api.solutionFactoryProvisioningApply(result.plan.id),
      ]);
      setPlan(detail.plan);
      setPlanRevisions(detail.revisions);
      setApplyState(applyResult);
      setInstance(null);
      setActivation(null);
      setNotice(
        result.replayed
          ? `Plan existant chargé · v${detail.plan.version}.`
          : `Plan créé · v${detail.plan.version}. Aucun runtime activé.`,
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de créer ou charger le plan.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function refreshPlan() {
    if (!api || !plan) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.refreshSolutionFactoryProvisioningPlan(
        plan.id,
        plan.version,
      );
      const [detail, applyResult] = await Promise.all([
        api.solutionFactoryProvisioningPlan(result.plan.id),
        api.solutionFactoryProvisioningApply(result.plan.id),
      ]);
      setPlan(detail.plan);
      setPlanRevisions(detail.revisions);
      setApplyState(applyResult);
      setNotice(
        `Readiness recalculé · plan v${detail.plan.version} · ${detail.plan.status}.`,
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Le plan a changé ailleurs. Recharge son état avant de continuer."
          : cause instanceof PlatformApiError
            ? cause.message
            : "Impossible de rafraîchir le plan.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function refreshApplyState(planId: string) {
    if (!api) return;
    const [detail, applyResult] = await Promise.all([
      api.solutionFactoryProvisioningPlan(planId),
      api.solutionFactoryProvisioningApply(planId),
    ]);
    setPlan(detail.plan);
    setPlanRevisions(detail.revisions);
    setApplyState(applyResult);
  }

  async function applyAgentProvisioning(
    descriptor: SolutionFactoryProvisioningApplyDescriptor,
  ) {
    if (
      !api ||
      !plan ||
      descriptor.actionKind !== "agent.configure-profile" ||
      !descriptor.profileKey ||
      !agentModel.trim()
    ) {
      setError("Renseigne le modèle Agent avant d'appliquer cette configuration.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.applySolutionFactoryProvisioning(
        plan.id,
        plan.version,
        {
          kind: "agent.configure-profile",
          profileKey: descriptor.profileKey,
          provider: "openai-responses",
          model: agentModel.trim(),
          ...(agentCredential.trim()
            ? { credential: agentCredential.trim() }
            : {}),
          reasoningEffort: descriptor.profileKey === "voice" ? "none" : "low",
          serviceTier: descriptor.profileKey === "voice" ? "fast" : "default",
        },
        "studio-agent-" + crypto.randomUUID(),
      );
      setAgentCredential("");
      await refreshApplyState(result.plan.id);
      setNotice(
        `Agent ${descriptor.profileKey} appliqué · readiness recalculé. Aucun runtime activé.`,
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Le plan a changé ailleurs. Recharge sa readiness avant de continuer."
          : cause instanceof PlatformApiError
            ? cause.message
            : "Impossible d'appliquer la configuration Agent.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function applyKnowledgeProvisioning() {
    if (!api || !plan || !knowledgeTitle.trim() || !knowledgeText.trim()) {
      setError("Renseigne un titre et un contenu Knowledge approuvé.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.applySolutionFactoryProvisioning(
        plan.id,
        plan.version,
        {
          kind: "knowledge.ingest-approved",
          title: knowledgeTitle.trim(),
          text: knowledgeText.trim(),
        },
        "studio-knowledge-" + crypto.randomUUID(),
      );
      setKnowledgeTitle("");
      setKnowledgeText("");
      await refreshApplyState(result.plan.id);
      setNotice("Knowledge approuvé appliqué · readiness recalculé. Aucun runtime activé.");
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Le plan a changé ailleurs. Recharge sa readiness avant de continuer."
          : cause instanceof PlatformApiError
            ? cause.message
            : "Impossible d'appliquer le Knowledge approuvé.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function materializeInstance() {
    if (!api || !plan || plan.status !== "ready_for_future_activation") return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createSolutionFactoryInstance(
        plan.id,
        plan.version,
      );
      const state = await api.solutionFactoryActivationState(result.instance.id);
      setInstance(result.instance);
      setInstances((current) => {
        const rest = current.filter((candidate) => candidate.id !== result.instance.id);
        return [result.instance, ...rest];
      });
      setActivation(state);
      setNotice(
        result.replayed
          ? "Instance préparée existante chargée. Runtime inchangé."
          : "Instance préparée créée. Elle reste inactive jusqu'à activation explicite.",
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de matérialiser l'instance.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function activateInstance() {
    if (!api || !instance) return;
    const control = activation?.control ?? null;
    if (
      control?.status === "active" &&
      control.selectedSolutionInstanceId !== instance.id
    ) {
      setError(
        "Une autre instance est active. Charge-la et désactive-la avant de basculer.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.activateSolutionFactoryInstance(
        instance.id,
        control?.version ?? null,
      );
      const state = await api.solutionFactoryActivationState(instance.id);
      setActivation(state);
      setNotice(
        state.runtimeAuthorization.allowed
          ? `Runtime actif · control v${state.control?.version ?? "?"}.`
          : "L'activation n'a pas produit d'autorité runtime.",
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Le contrôle d'activation a changé ailleurs. Recharge l'état avant de continuer."
          : cause instanceof PlatformApiError
            ? cause.message
            : "Impossible d'activer cette instance.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function deactivateInstance() {
    if (!api || !instance || !activation?.control) return;
    const control = activation.control;
    if (
      control.status !== "active" ||
      control.selectedSolutionInstanceId !== instance.id
    ) {
      setError("Cette instance n'est pas l'autorité runtime active.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.deactivateSolutionFactoryInstance(
        instance.id,
        control.version,
      );
      const state = await api.solutionFactoryActivationState(instance.id);
      setActivation(state);
      setNotice(
        `Runtime désactivé · control v${state.control?.version ?? "?"}. Les providers et données métier restent intacts.`,
      );
    } catch (cause) {
      setError(
        cause instanceof PlatformApiError && cause.code === "version_conflict"
          ? "Le contrôle d'activation a changé ailleurs. Recharge l'état avant de continuer."
          : cause instanceof PlatformApiError
            ? cause.message
            : "Impossible de désactiver cette instance.",
      );
    } finally {
      setBusy(false);
    }
  }

  const blockers = plan?.preview.checks.filter(
    (check) => check.status !== "ready",
  ) ?? [];
  const selectedIsActive =
    Boolean(instance) &&
    activation?.control?.status === "active" &&
    activation.control.selectedSolutionInstanceId === instance?.id;
  const anotherInstanceActive =
    Boolean(instance) &&
    activation?.control?.status === "active" &&
    activation.control.selectedSolutionInstanceId !== instance?.id;

  return <div className="studio-operation-card">
    <div className="studio-operation-heading">
      <span><Workflow size={14} /> Opérations</span>
      <strong data-state={
        selectedIsActive
          ? "active"
          : activation?.control?.status === "inactive"
            ? "inactive"
            : instance
              ? "prepared"
              : plan?.status ?? "idle"
      }>
        {selectedIsActive
          ? "ACTIVE"
          : activation?.control?.status === "inactive"
            ? "INACTIVE"
            : instance
              ? "PREPARED"
              : plan
                ? plan.status.replaceAll("_", " ").toUpperCase()
                : "NO PLAN"}
      </strong>
    </div>

    {!certification
      ? <p>Certifie d'abord une version candidate pour ouvrir le flux opérationnel.</p>
      : <>
          <label>
            Organisation cible
            <select
              aria-label="Organisation cible du provisioning"
              value={organizationId}
              disabled={busy || loading}
              onChange={(event) => setOrganizationId(event.target.value)}
            >
              {targetOrganizations.length === 0 && <option value="">Aucune organisation disponible</option>}
              {targetOrganizations.map((organization) =>
                <option key={organization.id} value={organization.id}>
                  {organization.displayName}
                </option>
              )}
            </select>
          </label>
          <small className="studio-operation-proof">
            Certification {certification.id.slice(0, 18)}… · sélectionner une organisation ne déclenche aucune mutation.
          </small>

          {instances.length > 0 && <label>
            Instances préparées
            <select
              aria-label="Instance Solution Factory"
              value={instance?.id ?? ""}
              disabled={busy || loading}
              onChange={(event) => {
                const next = event.target.value;
                if (!next) {
                  setInstance(null);
                  setPlan(null);
                  setPlanRevisions([]);
                  setActivation(null);
                  setApplyState(null);
                  setNotice("Certification actuelle sélectionnée. Aucune mutation exécutée.");
                  setError(null);
                  return;
                }
                void selectInstance(next);
              }}
            >
              <option value="">Certification actuelle / nouveau plan</option>
              {instances.map((candidate) =>
                <option key={candidate.id} value={candidate.id}>
                  {candidate.id.slice(0, 18)}… · {candidate.certificationId === certification.id ? "certification actuelle" : "historique"}
                </option>
              )}
            </select>
          </label>}

          {!plan && <button
            type="button"
            disabled={busy || loading || !organizationId}
            onClick={() => void createOrOpenPlan()}
          >
            Créer / ouvrir le plan
          </button>}

          {plan && <div className="studio-operation-stage">
            <div>
              <span>Provisioning plan</span>
              <strong>v{plan.version} · {plan.status.replaceAll("_", " ")}</strong>
            </div>
            <p>
              {plan.preview.summary.ready} ready · {plan.preview.summary.blocked} blocked · {plan.preview.summary.manualReview} manual review
            </p>
            {blockers.length > 0 && <ul>
              {blockers.slice(0, 4).map((check) =>
                <li key={check.key}>
                  <strong>{check.label}</strong>
                  <span>{check.detail}</span>
                </li>
              )}
            </ul>}
            <button type="button" disabled={busy} onClick={() => void refreshPlan()}>
              Rafraîchir readiness
            </button>
            {planRevisions.length > 1 && <small>{planRevisions.length} révisions du plan conservées.</small>}
          </div>}

          {plan && !instance && applyState && <div className="studio-operation-stage studio-provisioning-apply">
            <div>
              <span>Provisioning Apply</span>
              <strong>{applyState.actions.filter((action) => action.mode === "apply").length} action(s) · {applyState.receipts.length} receipt(s)</strong>
            </div>
            <p>
              Seules les mutations canoniques explicitement supportées sont exécutables ici. Les providers externes restent sous contrôle opérateur.
            </p>
            {applyState.actions.map((descriptor, index) =>
              <div className="studio-provisioning-action" key={descriptor.checkKey + ":" + (descriptor.profileKey ?? index)}>
                <div>
                  <strong>{descriptor.label}</strong>
                  <span>{descriptor.detail}</span>
                </div>
                {descriptor.mode === "operator_required"
                  ? <small className="studio-operation-warning">OPERATOR REQUIRED</small>
                  : descriptor.actionKind === "agent.configure-profile"
                    ? <div className="studio-provisioning-form">
                        <label>
                          Modèle
                          <input
                            value={agentModel}
                            placeholder="Modèle canonique"
                            disabled={busy}
                            onChange={(event) => setAgentModel(event.target.value)}
                          />
                        </label>
                        <label>
                          Credential
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={agentCredential}
                            placeholder="Optionnel si déjà configuré"
                            disabled={busy}
                            onChange={(event) => setAgentCredential(event.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          disabled={busy || !agentModel.trim()}
                          onClick={() => void applyAgentProvisioning(descriptor)}
                        >
                          Appliquer Agent {descriptor.profileKey}
                        </button>
                      </div>
                    : descriptor.actionKind === "knowledge.ingest-approved"
                      ? <div className="studio-provisioning-form">
                          <label>
                            Titre
                            <input
                              value={knowledgeTitle}
                              disabled={busy}
                              onChange={(event) => setKnowledgeTitle(event.target.value)}
                            />
                          </label>
                          <label>
                            Knowledge approuvé
                            <textarea
                              rows={4}
                              value={knowledgeText}
                              disabled={busy}
                              onChange={(event) => setKnowledgeText(event.target.value)}
                            />
                          </label>
                          <button
                            type="button"
                            disabled={busy || !knowledgeTitle.trim() || !knowledgeText.trim()}
                            onClick={() => void applyKnowledgeProvisioning()}
                          >
                            Appliquer Knowledge
                          </button>
                        </div>
                      : null}
              </div>
            )}
            {applyState.actions.length === 0 && <small>
              Aucun Apply restant. Le plan reflète déjà la vérité canonique disponible.
            </small>}
            {applyState.receipts.length > 0 && <div className="studio-operation-history">
              <span>Receipts Provisioning Apply</span>
              <ol>
                {applyState.receipts.slice(0, 6).map((receipt) =>
                  <li key={receipt.id}>
                    <strong>{receipt.status} · {receipt.actionKind}</strong>
                    <span>plan v{receipt.provisioningPlanVersion} · {receipt.id.slice(0, 16)}…</span>
                  </li>
                )}
              </ol>
            </div>}
          </div>}

          {plan?.status === "ready_for_future_activation" && !instance && <div className="studio-operation-stage">
            <strong>Plan prêt</strong>
            <p>La prochaine étape crée une identité durable préparée. Elle n'active pas le runtime.</p>
            <button type="button" disabled={busy} onClick={() => void materializeInstance()}>
              Matérialiser l'instance
            </button>
          </div>}

          {instance && <div className="studio-operation-stage">
            <div>
              <span>Solution Instance</span>
              <strong>{instance.id.slice(0, 22)}…</strong>
            </div>
            <p>
              {instance.lifecycleState} · plan v{instance.provisioningPlanVersion}
            </p>
            <div className="studio-operation-runtime" data-active={selectedIsActive ? "true" : "false"}>
              <span>Runtime authority</span>
              <strong>{activation?.runtimeAuthorization.mode ?? "loading"}</strong>
              {activation?.control && <small>
                control v{activation.control.version} · sélection {activation.control.selectedSolutionInstanceId.slice(0, 18)}…
              </small>}
            </div>

            {selectedIsActive
              ? <button
                  type="button"
                  className="studio-operation-danger"
                  disabled={busy}
                  onClick={() => void deactivateInstance()}
                >
                  Désactiver le runtime
                </button>
              : <button
                  type="button"
                  disabled={busy || anotherInstanceActive}
                  onClick={() => void activateInstance()}
                >
                  {activation?.control?.status === "inactive"
                    ? activation.control.selectedSolutionInstanceId === instance.id
                      ? "Réactiver cette instance"
                      : "Basculer vers cette instance"
                    : "Activer cette instance"}
                </button>}

            {anotherInstanceActive && <small className="studio-operation-warning">
              Une autre instance est active. Désactive d'abord l'instance sélectionnée par le control.
            </small>}

            {activation && activation.revisions.length > 0 && <div className="studio-operation-history">
              <span>Historique de contrôle</span>
              <ol>
                {activation.revisions.slice(0, 6).map((revision) =>
                  <li key={revision.version}>
                    <strong>v{revision.version} · {revision.action}</strong>
                    <span>{revision.status} · {revision.selectedSolutionInstanceId.slice(0, 16)}…</span>
                  </li>
                )}
              </ol>
            </div>}
          </div>}
        </>}

    {loading && <p className="studio-operation-feedback">Chargement de l'état opérationnel…</p>}
    {notice && <p className="studio-operation-feedback success" role="status">{notice}</p>}
    {error && <p className="studio-operation-feedback error" role="alert">{error}</p>}
  </div>;
}

export function SolutionStudio({
  api,
  targetOrganizations = [],
  liveEvents,
  liveConnection,
  liveEventStreamUrl = "/api/v1/platform/pulse/events",
}: {
  api?: PlatformApiClient;
  targetOrganizations?: readonly SolutionStudioTargetOrganization[];
  liveEvents?: readonly PulseExecutionEvent[];
  liveConnection?: LiveConnectionState;
  liveEventStreamUrl?: string | null;
}) {
  const [mode, setMode] = useState<StudioMode>("build");
  const [selectedNode, setSelectedNode] = useState<SolutionNodeKey>("agent");
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [inspectorView, setInspectorView] = useState<"node" | "solution" | "execution">("node");
  const [simulationEvents, setSimulationEvents] = useState<readonly PulseExecutionEvent[]>([]);
  const [simulationRun, setSimulationRun] = useState(0);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [streamEvents, setStreamEvents] = useState<readonly PulseExecutionEvent[]>([]);
  const [streamConnection, setStreamConnection] = useState<LiveConnectionState>("disconnected");
  const [selectedCorrelationId, setSelectedCorrelationId] = useState<string | null>(null);
  const [followLive, setFollowLive] = useState(true);
  const [draft, setDraft] = useState<SolutionFactoryDraft | null>(null);
  const [drafts, setDrafts] = useState<readonly SolutionFactoryDraft[]>([]);
  const [certifications, setCertifications] = useState<readonly SolutionFactoryCertification[]>([]);
  const [workingBlueprint, setWorkingBlueprint] = useState<SolutionBlueprintV1>(
    () => updateSolutionLifecycle(SOLUTION_BLUEPRINT, "draft"),
  );
  const [draftName, setDraftName] = useState(SOLUTION_BLUEPRINT.displayName);
  const [draftDescription, setDraftDescription] = useState(SOLUTION_BLUEPRINT.description);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftValidation, setDraftValidation] = useState<"valid" | "invalid" | null>(null);
  const externallyControlledLive = liveEvents !== undefined || liveConnection !== undefined;
  const effectiveLiveEvents = liveEvents ?? streamEvents;
  const effectiveLiveConnection = liveConnection ?? streamConnection;

  const safeLiveEvents = useMemo(() => runtimeEventsOnly(effectiveLiveEvents), [effectiveLiveEvents]);
  const liveTraces = useMemo(() => runtimeTraceSummaries(safeLiveEvents), [safeLiveEvents]);
  const selectedLiveEvents = useMemo(
    () => eventsForCorrelation(safeLiveEvents, selectedCorrelationId),
    [safeLiveEvents, selectedCorrelationId],
  );
  const liveSummary = useMemo(
    () => summarizeExecution(selectedLiveEvents),
    [selectedLiveEvents],
  );
  const visibleEvents = mode === "simulate"
    ? simulationEvents
    : mode === "live"
      ? selectedLiveEvents
      : [];

  const editableBlueprint = useMemo<SolutionBlueprintV1>(() => ({
    ...workingBlueprint,
    lifecycle: workingBlueprint.lifecycle === "candidate" ? "candidate" : "draft",
    displayName: draftName.trim(),
    description: draftDescription.trim(),
  }), [workingBlueprint, draftName, draftDescription]);

  const demoScenario = useMemo(
    () => buildSolutionDemoScenario(
      editableBlueprint,
      Math.max(1, simulationRun),
    ),
    [editableBlueprint, simulationRun],
  );
  const demoSummary = useMemo(
    () => summarizeExecution(simulationEvents),
    [simulationEvents],
  );

  const graphProjection = useMemo(
    () => projectSolutionBlueprint(workingBlueprint),
    [workingBlueprint],
  );

  const draftDirty = draft
    ? JSON.stringify(draft.blueprint) !== JSON.stringify(editableBlueprint)
    : true;

  const currentCertification = draft
    ? certifications.find(
        (certification) =>
          certification.sourceDraftId === draft.id &&
          certification.sourceDraftVersion === draft.version,
      ) ?? null
    : null;

  const draftCertifications = draft
    ? certifications.filter(
        (certification) => certification.sourceDraftId === draft.id,
      )
    : [];

  useEffect(() => {
    if (!api) return;
    let active = true;
    setDraftLoading(true);
    setDraftError(null);
    Promise.all([
      api.solutionFactoryDrafts(100),
      api.solutionFactoryCertifications(100),
    ])
      .then(([draftResult, certificationResult]) => {
        if (!active) return;
        setDrafts(draftResult.drafts);
        setCertifications(certificationResult.certifications);

        const requestedDraftId =
          new URLSearchParams(window.location.search).get("draft");
        if (
          requestedDraftId &&
          /^cd_sfd_[a-f0-9]{32}$/.test(requestedDraftId)
        ) {
          const requestedDraft = draftResult.drafts.find(
            (candidate) => candidate.id === requestedDraftId,
          );
          if (requestedDraft) {
            setDraft(requestedDraft);
            setWorkingBlueprint(requestedDraft.blueprint);
            setDraftName(requestedDraft.blueprint.displayName);
            setDraftDescription(requestedDraft.blueprint.description);
            setDraftValidation("valid");
            setDraftNotice(
              `Prospect Demo draft ouvert · version ${requestedDraft.version}.`,
            );
          } else {
            setDraftError(
              "Le draft Prospect Demo demandé n'existe plus.",
            );
          }
        }
      })
      .catch((cause) => {
        if (!active) return;
        setDraftError(
          cause instanceof PlatformApiError
            ? cause.message
            : "Impossible de charger les drafts Solution Factory.",
        );
      })
      .finally(() => {
        if (active) setDraftLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    if (graphProjection.nodes.some((node) => node.key === selectedNode)) return;
    const fallback = graphProjection.nodes[0];
    if (fallback) setSelectedNode(fallback.key);
  }, [graphProjection, selectedNode]);

  useEffect(() => {
    if (mode !== "live" || liveTraces.length === 0) return;
    const latest = liveTraces[0]!.correlationId;
    if (followLive || !selectedCorrelationId || !liveTraces.some((trace) => trace.correlationId === selectedCorrelationId)) {
      setSelectedCorrelationId(latest);
    }
  }, [mode, liveTraces, followLive, selectedCorrelationId]);

  useEffect(() => {
    if (mode !== "live" || externallyControlledLive || !liveEventStreamUrl) return;
    if (typeof EventSource === "undefined") {
      setStreamConnection("disconnected");
      return;
    }

    setStreamConnection("connecting");
    const source = new EventSource(liveEventStreamUrl, { withCredentials: true });
    source.onopen = () => setStreamConnection("connected");
    source.onerror = () => {
      setStreamConnection(source.readyState === EventSource.CLOSED ? "disconnected" : "connecting");
    };
    source.onmessage = (message) => {
      try {
        const parsed = JSON.parse(message.data) as Partial<PulseExecutionEvent>;
        if (
          parsed.origin !== "runtime" ||
          typeof parsed.eventId !== "string" ||
          typeof parsed.correlationId !== "string" ||
          typeof parsed.nodeKey !== "string" ||
          typeof parsed.label !== "string" ||
          typeof parsed.detail !== "string" ||
          !["started", "completed", "failed", "handoff"].includes(parsed.phase ?? "")
        ) return;
        const event = parsed as PulseExecutionEvent;
        setStreamEvents((current) => {
          if (current.some((candidate) => candidate.eventId === event.eventId)) return current;
          return [...current, event].slice(-100);
        });
      } catch {
        // Ignore malformed observability frames; Live must fail closed rather than invent state.
      }
    };

    return () => {
      source.close();
      setStreamConnection("disconnected");
    };
  }, [mode, externallyControlledLive, liveEventStreamUrl]);

  useEffect(() => {
    if (!simulationRunning || mode !== "simulate") return;
    const timers = demoScenario.events.map((event) => window.setTimeout(() => {
      setSimulationEvents((current) => [...current, event]);
    }, event.offsetMs ?? 0));
    const finalEvent = demoScenario.events.at(-1);
    const finalOffset = finalEvent?.offsetMs ?? 0;
    const finalDuration = finalEvent?.durationMs ?? 0;
    timers.push(window.setTimeout(
      () => setSimulationRunning(false),
      finalOffset + finalDuration + 500,
    ));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [demoScenario, simulationRunning, mode]);

  function openDraft(candidate: SolutionFactoryDraft) {
    setDraft(candidate);
    setWorkingBlueprint(candidate.blueprint);
    setDraftName(candidate.blueprint.displayName);
    setDraftDescription(candidate.blueprint.description);
    setDraftValidation("valid");
    setDraftNotice(`Draft ouvert · version ${candidate.version}.`);
    setDraftError(null);
  }

  function newDraft() {
    setDraft(null);
    setWorkingBlueprint(updateSolutionLifecycle(SOLUTION_BLUEPRINT, "draft"));
    setDraftName(SOLUTION_BLUEPRINT.displayName);
    setDraftDescription(SOLUTION_BLUEPRINT.description);
    setDraftValidation(null);
    setDraftNotice("Nouveau draft local · sauvegarde nécessaire pour le rendre durable.");
    setDraftError(null);
  }

  async function validateDraft() {
    if (!api) {
      setDraftError("La persistance Solution Factory n'est pas connectée.");
      return;
    }
    setDraftBusy(true);
    setDraftError(null);
    setDraftNotice(null);
    try {
      const result = await api.validateSolutionFactoryBlueprint(editableBlueprint);
      if (result.validation.valid) {
        setDraftValidation("valid");
        setDraftNotice("Blueprint valide · aucune action externe exécutée.");
      } else {
        setDraftValidation("invalid");
        setDraftError(
          result.validation.issues[0]?.message ??
            "Le blueprint ne respecte pas le contrat Solution Factory.",
        );
      }
    } catch (cause) {
      setDraftValidation("invalid");
      setDraftError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de valider le blueprint.",
      );
    } finally {
      setDraftBusy(false);
    }
  }

  async function saveDraft() {
    if (!api) {
      setDraftError("La persistance Solution Factory n'est pas connectée.");
      return;
    }
    setDraftBusy(true);
    setDraftError(null);
    setDraftNotice(null);
    try {
      const result = draft
        ? await api.updateSolutionFactoryDraft(
            draft.id,
            editableBlueprint,
            draft.version,
          )
        : await api.createSolutionFactoryDraft(editableBlueprint);
      setDraft(result.draft);
      setWorkingBlueprint(result.draft.blueprint);
      setDrafts((current) => {
        const withoutCurrent = current.filter((candidate) => candidate.id !== result.draft.id);
        return [result.draft, ...withoutCurrent].slice(0, 20);
      });
      setDraftName(result.draft.blueprint.displayName);
      setDraftDescription(result.draft.blueprint.description);
      setDraftValidation("valid");
      setDraftNotice(
        draft
          ? `Draft sauvegardé · version ${result.draft.version}.`
          : `Draft créé · version ${result.draft.version}.`,
      );
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.code === "version_conflict") {
        setDraftError("Ce draft a changé ailleurs. Recharge Studio avant de sauvegarder.");
      } else {
        setDraftError(
          cause instanceof PlatformApiError
            ? cause.message
            : "Impossible de sauvegarder le draft.",
        );
      }
    } finally {
      setDraftBusy(false);
    }
  }

  async function changeLifecycle(next: "draft" | "candidate") {
    if (!api || !draft || draftDirty) {
      setDraftError(
        "Sauvegarde d'abord le draft avant de changer son lifecycle.",
      );
      return;
    }
    setDraftBusy(true);
    setDraftError(null);
    setDraftNotice(null);
    try {
      const blueprint = updateSolutionLifecycle(draft.blueprint, next);
      const validation = await api.validateSolutionFactoryBlueprint(blueprint);
      if (!validation.validation.valid) {
        setDraftValidation("invalid");
        setDraftError(
          validation.validation.issues[0]?.message ??
            "Le blueprint n'est pas prêt pour ce lifecycle.",
        );
        return;
      }

      const result = await api.updateSolutionFactoryDraft(
        draft.id,
        blueprint,
        draft.version,
      );
      setDraft(result.draft);
      setWorkingBlueprint(result.draft.blueprint);
      setDrafts((current) => {
        const withoutCurrent = current.filter(
          (candidate) => candidate.id !== result.draft.id,
        );
        return [result.draft, ...withoutCurrent].slice(0, 20);
      });
      setDraftValidation("valid");
      setDraftNotice(
        next === "candidate"
          ? `Candidate enregistré · version ${result.draft.version}. Aucun runtime n'a été activé.`
          : `Repassé en draft · version ${result.draft.version}.`,
      );
    } catch (cause) {
      setDraftError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de changer le lifecycle du draft.",
      );
    } finally {
      setDraftBusy(false);
    }
  }

  async function certifyCurrentDraft() {
    if (
      !api ||
      !draft ||
      draftDirty ||
      workingBlueprint.lifecycle !== "candidate"
    ) {
      setDraftError(
        "La certification exige une version candidate sauvegardée et sans modification locale.",
      );
      return;
    }
    setDraftBusy(true);
    setDraftError(null);
    setDraftNotice(null);
    try {
      const result = await api.certifySolutionFactoryDraft(
        draft.id,
        draft.version,
      );
      setCertifications((current) => {
        const withoutCurrent = current.filter(
          (item) => item.id !== result.certification.id,
        );
        return [result.certification, ...withoutCurrent].slice(0, 100);
      });
      setDraftNotice(
        `Certification immuable créée · ${result.certification.id}. Aucun déploiement n'a été déclenché.`,
      );
    } catch (cause) {
      setDraftError(
        cause instanceof PlatformApiError
          ? cause.message
          : "Impossible de certifier cette version.",
      );
    } finally {
      setDraftBusy(false);
    }
  }

  function runSimulation() {
    setMode("simulate");
    setInspectorView("execution");
    setSimulationEvents([]);
    setSimulationRunning(false);
    window.setTimeout(() => {
      setSimulationRun((value) => value + 1);
      setSimulationRunning(true);
    }, 0);
  }

  const selected = graphProjection.nodes.find((node) => node.key === selectedNode)
    ?? SOLUTION_NODES.find((node) => node.key === selectedNode)!;
  const liveLabel = effectiveLiveConnection === "connected"
    ? selectedCorrelationId
      ? `${selectedLiveEvents.length} événements · trace active`
      : `${safeLiveEvents.length} événements runtime reçus`
    : effectiveLiveConnection === "connecting"
      ? "Connexion au flux runtime…"
      : "Aucune source runtime connectée";

  return <section className="solution-studio" aria-label="ClickDialog Solution Studio">
    <header className="solution-studio-header">
      <div className="studio-title-group">
        <div className="solution-studio-kicker"><Sparkles size={14} aria-hidden="true" /> Solution Studio</div>
        <div className="studio-title-row">
          <h1>{mode === "build" && !draft ? "Solution Builder" : draftName || SOLUTION_BLUEPRINT.displayName}</h1>
          <span className="studio-save-state" data-dirty={draftDirty ? "true" : "false"}>
            {draftLoading
              ? "Chargement…"
              : draft
                ? `Draft v${draft.version} · ${draftDirty ? "non sauvegardé" : "sauvegardé"}`
                : mode === "build"
                  ? "Working Canvas"
                  : "Aucun Blueprint compilé"}
          </span>
        </div>
      </div>

      <div className="studio-mode-switcher" role="group" aria-label="Mode du Solution Studio">
        <button
          type="button"
          aria-pressed={mode === "build"}
          onClick={() => {
            setMode("build");
            setInspectorView("node");
          }}
        ><Wrench size={16} /> Build</button>
        <button
          type="button"
          aria-pressed={mode === "simulate"}
          disabled={!draft}
          title={draft ? "Tester le Blueprint compilé" : "Compile d'abord le Working Canvas"}
          onClick={() => {
            setMode("simulate");
            setInspectorView("execution");
                  }}
        ><Play size={16} /> Demo</button>
        <button
          type="button"
          aria-pressed={mode === "live"}
          onClick={() => {
            setMode("live");
            setInspectorView("execution");
                  }}
        ><Radio size={16} /> Live</button>
      </div>

      <div className="studio-primary-action">
        {mode === "build" && draft && <button
          type="button"
          className="studio-run-button"
          onClick={() => {
            setInspectorView("solution");
            setInspectorOpen(true);
          }}
        >
          <BadgeCheck size={15} /> Lifecycle
        </button>}
        {mode === "simulate" && <button type="button" className="studio-run-button" disabled={simulationRunning} onClick={runSimulation}>
          <Play size={15} fill="currentColor" /> {simulationRunning
            ? "Demo en cours…"
            : simulationEvents.length === 0
              ? "Lancer la démo"
              : "Relancer la démo"}
        </button>}
        {mode === "live" && <div className="studio-live-actions">
          <button
            type="button"
            className="studio-follow-button"
            aria-pressed={followLive}
            onClick={() => setFollowLive((value) => !value)}
          >
            <Radio size={13} /> {followLive ? "Following live" : "Resume live"}
          </button>
          <span className="studio-live-proof">REAL</span>
        </div>}
      </div>
    </header>

    <div className="studio-context-bar" data-mode={mode}>
      <span className="studio-status-dot" aria-hidden="true" />
      <strong>{mode === "build" ? "BUILD" : mode === "simulate" ? "DEMO · SYNTHETIC" : "LIVE · REAL"}</strong>
      <span>{mode === "build"
        ? "Working Canvas libre · arrastra componentes, conecta, ramifica y guarda antes de compilar."
        : mode === "simulate"
          ? `Blueprint-derived · ${demoSummary.nodeCount}/${demoScenario.events.length} módulos recorridos`
          : liveLabel}</span>
    </div>

    <div className="solution-studio-workbench">
      {mode === "build"
        ? <FreeformSolutionCanvas
            api={api}
            onCompiledDraft={(compiled) => {
              setDraft(compiled);
              setWorkingBlueprint(compiled.blueprint);
              setDraftName(compiled.blueprint.displayName);
              setDraftDescription(compiled.blueprint.description);
              setDraftValidation("valid");
              setDrafts((current) => [
                compiled,
                ...current.filter((candidate) => candidate.id !== compiled.id),
              ].slice(0, 100));
              setDraftNotice(
                `Blueprint compilado · Draft v${compiled.version}. Ya podés promoverlo a Candidate o probarlo en Demo.`,
              );
              setDraftError(null);
              setInspectorView("solution");
              setInspectorOpen(true);
            }}
          />
        : <section className="studio-canvas">
        <div className="studio-canvas-toolbar">
          <div className="studio-canvas-toolbar-left">
            <span className="studio-template-badge">
              {currentCertification
                ? `Certified v${currentCertification.sourceDraftVersion}`
                : draft
                  ? `Draft v${draft.version}`
                  : "Template"}
            </span>
            <strong>{draftName || SOLUTION_BLUEPRINT.displayName}</strong>
          </div>
          <div className="studio-canvas-toolbar-right">
            <span className="studio-canvas-hint">
              {mode === "live"
                ? "Eventos reales de runtime"
                : mode === "simulate"
                  ? "Ejecución sintética sobre este mismo grafo"
                  : "Arrastra nodos · selecciona para configurar"}
            </span>
            <button
              type="button"
              className="studio-tool-button"
              aria-pressed={inspectorOpen}
              onClick={() => setInspectorOpen((value) => !value)}
            >
              <Settings2 size={15} /> Inspector
            </button>
          </div>
        </div>

        <PulseGraph
          events={visibleEvents}
          nodes={mode === "live" && !draft ? SOLUTION_NODES : graphProjection.nodes}
          edges={mode === "live" && !draft ? SOLUTION_EDGES : graphProjection.edges}
          selectedNode={selectedNode}
          editable={false}
          zoom={canvasZoom}
          onSelectNode={(node) => {
            setSelectedNode(node);
            setInspectorView("node");
            setInspectorOpen(true);
          }}
        />

        <div className="studio-canvas-controls" role="group" aria-label="Controles du canvas">
          <button
            type="button"
            aria-label="Réduire le zoom"
            disabled={canvasZoom <= 0.7}
            onClick={() => setCanvasZoom((value) => Math.max(0.7, Math.round((value - 0.1) * 10) / 10))}
          ><ZoomOut size={15} /></button>
          <span>{Math.round(canvasZoom * 100)}%</span>
          <button
            type="button"
            aria-label="Augmenter le zoom"
            disabled={canvasZoom >= 1.4}
            onClick={() => setCanvasZoom((value) => Math.min(1.4, Math.round((value + 0.1) * 10) / 10))}
          ><ZoomIn size={15} /></button>
          <button
            type="button"
            aria-label="Ajuster le canvas"
            onClick={() => setCanvasZoom(1)}
          ><Maximize2 size={15} /></button>
        </div>

        <div className="studio-execution-dock" data-mode={mode}>
          <div className="studio-execution-dock-status">
            <span className="studio-status-dot" aria-hidden="true" />
            <div>
              <small>{mode === "simulate" ? "DEMO EXECUTION" : "LIVE EXECUTION"}</small>
              <strong>
                {mode === "simulate"
                  ? demoSummary.status.toUpperCase()
                  : selectedLiveEvents.length > 0
                    ? liveSummary.status.toUpperCase()
                    : effectiveLiveConnection === "connected"
                      ? "LISTENING"
                      : "WAITING"}
              </strong>
            </div>
          </div>
          <div className="studio-execution-metrics">
            <span><strong>{mode === "simulate" ? demoSummary.nodeCount : liveSummary.nodeCount}</strong> nodos</span>
            <span><strong>{mode === "simulate" ? demoSummary.eventCount : liveSummary.eventCount}</strong> eventos</span>
            <span><strong>{mode === "simulate" ? demoSummary.totalDurationMs : liveSummary.totalDurationMs}</strong> ms</span>
          </div>
          <button type="button" onClick={() => {
            setInspectorView("execution");
            setInspectorOpen(true);
          }}>
            Ver ejecución
          </button>
        </div>
      </section>}

      {inspectorOpen && (mode !== "build" || inspectorView === "solution") && <aside className="studio-inspector" aria-label="Inspector de solución">
        <div className="studio-inspector-tabs" role="tablist" aria-label="Inspector">
          {mode !== "build" && <button
            type="button"
            role="tab"
            aria-selected={inspectorView === "node"}
            onClick={() => setInspectorView("node")}
          >
            Nodo
          </button>}
          {mode === "build"
            ? <button
                type="button"
                role="tab"
                aria-selected={inspectorView === "solution"}
                onClick={() => setInspectorView("solution")}
              >
                <Settings2 size={14} /> Lifecycle
              </button>
            : <button
                type="button"
                role="tab"
                aria-selected={inspectorView === "execution"}
                onClick={() => setInspectorView("execution")}
              >
                <Radio size={14} /> Execution
              </button>}
          <button
            type="button"
            className="studio-inspector-close"
            aria-label="Fermer l'inspecteur"
            onClick={() => setInspectorOpen(false)}
          ><X size={15} /></button>
        </div>

        <div className="studio-inspector-scroll">
          {mode !== "build" && inspectorView === "node" && <div className="studio-inspector-section">
            <p className="studio-panel-label">Selected node</p>
            <div className="studio-inspector-title">
              <span className="solution-node-icon" aria-hidden="true">{(() => { const Icon = iconForNode(selected); return <Icon size={18} strokeWidth={1.8} />; })()}</span>
              <div><strong>{selected.label}</strong><span>{selected.eyebrow}</span></div>
            </div>
            <p>{selected.description}</p>

            <dl className="studio-node-facts">
              <div><dt>Type</dt><dd>{selected.kind}</dd></div>
              {selected.capabilityKey && <div><dt>Capability</dt><dd>{selected.capabilityKey}</dd></div>}
              {selected.channelKey && <div><dt>Channel</dt><dd>{selected.channelKey}</dd></div>}
            </dl>

            <div className="studio-node-execution">
              <span>Actividad en esta ejecución</span>
              {visibleEvents.filter((event) => event.nodeKey === selectedNode).length === 0
                ? <small>Este nodo todavía no fue recorrido.</small>
                : visibleEvents
                    .filter((event) => event.nodeKey === selectedNode)
                    .slice(-3)
                    .map((event) => <div key={event.eventId} data-phase={event.phase}>
                      <strong>{event.label}</strong>
                      <small>{event.detail}</small>
                    </div>)}
            </div>
          </div>}

          {mode === "build" && inspectorView === "solution" && <div className="studio-solution-panel">
            <div className="studio-inspector-section">
              <p className="studio-panel-label">Compiled Blueprint</p>
              <div className="studio-solution-heading">
                <div>
                  <strong>{draft?.displayName ?? "Sin Blueprint compilado"}</strong>
                  <span>
                    {draft
                      ? `${workingBlueprint.nodes.length} módulos · ${workingBlueprint.edges.length} conexiones · Draft v${draft.version}`
                      : "Validate & Compile el Working Canvas para entrar al lifecycle."}
                  </span>
                </div>
              </div>
              {draft && <div className="studio-compiled-proof">
                <BadgeCheck size={15} />
                <div>
                  <strong>Working Canvas → Blueprint</strong>
                  <span>
                    Este Blueprint es la salida compilada. Para cambiar la solución, editá el canvas y volvé a compilar.
                  </span>
                </div>
              </div>}
              {draftNotice && <p className="studio-draft-feedback success" role="status">{draftNotice}</p>}
              {draftError && <p className="studio-draft-feedback error" role="alert">{draftError}</p>}
            </div>

            <div className="studio-inspector-section">
              <div className="studio-section-heading">
                <div>
                  <p className="studio-panel-label">Lifecycle</p>
                  <strong>{workingBlueprint.lifecycle === "candidate" ? "Candidate" : "Draft"}</strong>
                </div>
                {draft && <button
                  type="button"
                  disabled={draftBusy || draftDirty}
                  onClick={() => void changeLifecycle(
                    workingBlueprint.lifecycle === "candidate"
                      ? "draft"
                      : "candidate",
                  )}
                >
                  {workingBlueprint.lifecycle === "candidate"
                    ? "Volver a draft"
                    : "Promover a candidate"}
                </button>}
              </div>

              {draft && workingBlueprint.lifecycle === "candidate" && <div
                className="studio-certification-card"
                data-certified={currentCertification ? "true" : "false"}
              >
                <div className="studio-certification-heading">
                  <span><BadgeCheck size={14} /> Certification</span>
                  {currentCertification
                    ? <strong>CERTIFIED</strong>
                    : <strong>CANDIDATE</strong>}
                </div>
                {currentCertification
                  ? <>
                      <p>Versión {currentCertification.sourceDraftVersion} congelada e inmutable.</p>
                      <small>{currentCertification.blueprintHash.slice(0, 16)}…</small>
                    </>
                  : <>
                      <p>Congela esta composición como release certificada.</p>
                      <button
                        type="button"
                        disabled={draftBusy || draftDirty}
                        onClick={() => void certifyCurrentDraft()}
                      >
                        <BadgeCheck size={13} /> Certifier cette version
                      </button>
                    </>}
              </div>}
            </div>

            <div className="studio-inspector-section">
              <div className="studio-draft-workspace-heading">
                <span><Layers3 size={13} /> Versiones</span>
                <strong>{drafts.length}</strong>
              </div>
              {drafts.length === 0
                ? <p className="studio-draft-workspace-empty">Sin drafts persistidos.</p>
                : <div className="studio-draft-list" aria-label="Drafts récents">
                    {drafts.slice(0, 5).map((candidate) => <button
                      type="button"
                      key={candidate.id}
                      data-selected={candidate.id === draft?.id ? "true" : "false"}
                      disabled={draftBusy || draftLoading}
                      onClick={() => openDraft(candidate)}
                    >
                      <span>
                        <strong>{candidate.displayName}</strong>
                        <small>v{candidate.version} · {candidate.lifecycle}</small>
                      </span>
                      <span aria-hidden="true">→</span>
                    </button>)}
                  </div>}
            </div>

            {currentCertification && <div className="studio-inspector-section studio-install-section">
              <p className="studio-panel-label">Install / Runtime</p>
              <SolutionOperationsPanel
                api={api}
                certification={currentCertification}
                targetOrganizations={targetOrganizations}
              />
            </div>}
          </div>}

          {mode !== "build" && inspectorView === "execution" && <div className="studio-execution-panel">
            {mode === "simulate" && <div className="studio-inspector-section">
              <p className="studio-panel-label">Demo from Solution</p>
              <div className="studio-execution-hero" data-status={demoSummary.status}>
                <span>SYNTHETIC · BLUEPRINT-DERIVED</span>
                <strong>{demoSummary.status.toUpperCase()}</strong>
                <small>{demoScenario.label}</small>
              </div>
              <div className="studio-execution-grid">
                <div><strong>{demoSummary.nodeCount}</strong><span>Nodos</span></div>
                <div><strong>{demoSummary.eventCount}</strong><span>Eventos</span></div>
                <div><strong>{demoSummary.totalDurationMs}</strong><span>ms</span></div>
                <div><strong>{demoSummary.failureCount}</strong><span>Errores</span></div>
              </div>
            </div>}

            {mode === "live" && <div className="studio-inspector-section">
              <p className="studio-panel-label">Live execution</p>
              <div className="studio-execution-hero" data-status={liveSummary.status}>
                <span>REAL RUNTIME EVENTS</span>
                <strong>{selectedLiveEvents.length > 0 ? liveSummary.status.toUpperCase() : "LISTENING"}</strong>
                <small>{selectedCorrelationId ? compactCorrelationId(selectedCorrelationId) : liveLabel}</small>
              </div>
              {selectedLiveEvents.length > 0 && <div className="studio-execution-grid">
                <div><strong>{liveSummary.nodeCount}</strong><span>Nodos</span></div>
                <div><strong>{liveSummary.eventCount}</strong><span>Eventos</span></div>
                <div><strong>{liveSummary.totalDurationMs}</strong><span>ms</span></div>
                <div><strong>{liveSummary.failureCount}</strong><span>Errores</span></div>
              </div>}
            </div>}

            {mode === "live" && <div className="studio-inspector-section">
              <div className="studio-traces-heading">
                <p className="studio-panel-label">Runtime traces</p>
                <span>{liveTraces.length}</span>
              </div>
              {liveTraces.length === 0
                ? <p className="studio-traces-empty">Aucune trace réelle reçue.</p>
                : <div className="studio-trace-list">
                  {liveTraces.slice(0, 8).map((trace) => <button
                    type="button"
                    key={trace.correlationId}
                    data-selected={trace.correlationId === selectedCorrelationId ? "true" : "false"}
                    onClick={() => {
                      setSelectedCorrelationId(trace.correlationId);
                      setFollowLive(false);
                    }}
                  >
                    <span className="studio-trace-status" data-phase={trace.latestEvent.phase} aria-hidden="true" />
                    <span>
                      <strong>{compactCorrelationId(trace.correlationId)}</strong>
                      <small>{trace.eventCount} eventos · {trace.latestEvent.source ?? trace.latestEvent.nodeKey}</small>
                    </span>
                  </button>)}
                </div>}
            </div>}

            <div className="studio-inspector-section">
              <p className="studio-panel-label">Events</p>
              <EventTimeline
                events={visibleEvents}
                emptyMessage={mode === "live"
                  ? "Esperando eventos reales. Pulse no inventa estado."
                  : "Ejecuta la demo para recorrer esta solución."}
              />
            </div>
          </div>}
        </div>
      </aside>}
    </div>
  </section>;
}
